/**
 * Packing list rows built only from shipment-linked collections (boxes).
 */

const normKey = (s) => String(s || '').trim().toUpperCase();

/** RGB 0–255 for PDF / Excel fills */
export const CARGO_TYPE_COLORS = {
  'JET L BOX': [34, 197, 94],
  'JET M BOX': [22, 163, 74],
  REFRIGERATOR: [59, 130, 246],
  TV: [125, 211, 252],
  MATTRESS: [147, 51, 234],
  METTRESS: [147, 51, 234],
  PACKAGE: [168, 85, 247],
  'A/C - INDOOR': [239, 68, 68],
  'A/C - OUTDOOR': [220, 38, 38],
  'A\\C - INDOOR': [239, 68, 68],
  'A\\C - OUTDOOR': [220, 38, 38],
};

export const CLEARING_TYPE_COLORS = {
  UPB: [34, 197, 94],
  DP: [59, 130, 246],
  D2D: [234, 179, 8],
};

export function cargoTypeColorRgb(label) {
  const k = normKey(label);
  return CARGO_TYPE_COLORS[k] || [71, 85, 105];
}

export function clearingTypeColorRgb(label) {
  const k = normKey(label);
  return CLEARING_TYPE_COLORS[k] || [100, 116, 139];
}

/** CSS rgb() for web pills */
export function rgbCss(rgb) {
  const [r, g, b] = rgb;
  return `rgb(${r},${g},${b})`;
}

export function isConsolidatedParent(collection) {
  return Array.isArray(collection?.childCollectionIds) && collection.childCollectionIds.length > 0;
}

function boxQty(b) {
  const q = Number.parseInt(String(b.quantity), 10);
  return Number.isFinite(q) && q > 0 ? q : 1;
}

function sumBoxTotals(boxes) {
  let totalPcs = 0;
  let totalKg = 0;
  let totalCbm = 0;
  for (const b of boxes) {
    const qty = boxQty(b);
    totalPcs += qty;
    const w = parseFloat(String(b.weight || '').replace(/,/g, '')) || 0;
    const cbm = parseFloat(String(b.cbm || '').replace(/,/g, '')) || 0;
    totalKg += w * qty;
    totalCbm += cbm * qty;
  }
  return { totalPcs, totalKg, totalCbm };
}

function buildRegularPackingRows(c, cid) {
  const boxes = Array.isArray(c.boxes) ? c.boxes : [];
  const collectionNo = String(c.collectionNo || cid).trim();
  const clearingRaw = String(c.clearingType || '').trim();
  const clearingType = clearingRaw ? clearingRaw.toUpperCase() : '';
  const { totalPcs, totalKg, totalCbm } = sumBoxTotals(boxes);

  if (boxes.length === 0) {
    return [{
      key: `${cid}-empty`,
      collectionNo,
      subNo: '—',
      length: '',
      width: '',
      height: '',
      kg: '',
      cbm: '',
      cargoType: '',
      description: 'No boxes in collection',
      d2d: '',
      clearingType,
      pcs: String(totalPcs),
      fWeight: totalKg.toFixed(2),
      fCbm: totalCbm.toFixed(2),
      groupSize: 1,
      isFirstInGroup: true,
      isConsolidatedLayout: false,
    }];
  }

  const expanded = [];
  boxes.forEach((b, boxIdx) => {
    const qty = boxQty(b);
    for (let u = 0; u < qty; u += 1) {
      expanded.push({ b, boxIdx, u });
    }
  });

  const groupSize = expanded.length;

  return expanded.map((item, globalIdx) => {
    const { b } = item;
    const w = parseFloat(String(b.weight || '').replace(/,/g, '')) || 0;
    const cbm = parseFloat(String(b.cbm || '').replace(/,/g, '')) || 0;
    return {
      key: `${cid}-${item.boxIdx}-${item.u}`,
      collectionNo,
      subNo: String(globalIdx + 1),
      length: String(b.length ?? '').trim(),
      width: String(b.width ?? '').trim(),
      height: String(b.height ?? '').trim(),
      kg: w.toFixed(2),
      cbm: cbm.toFixed(2),
      cargoType: String(b.boxType || b.officialBoxName || '').trim(),
      description: String(b.description || '').trim(),
      d2d: String(b.d2d ?? '').trim(),
      clearingType,
      pcs: globalIdx === 0 ? String(totalPcs) : '',
      fWeight: globalIdx === 0 ? totalKg.toFixed(2) : '',
      fCbm: globalIdx === 0 ? totalCbm.toFixed(2) : '',
      groupSize,
      isFirstInGroup: globalIdx === 0,
      isConsolidatedLayout: false,
    };
  });
}

function buildConsolidatedPackingRows(parent, collectionsById) {
  const masterNo = String(parent.collectionNo || parent._id || '').trim();
  const childIds = (parent.childCollectionIds || []).map(String);
  const blockRows = [];
  let grandPcs = 0;
  let grandKg = 0;
  let grandCbm = 0;
  let globalSubNo = 0;

  for (const childId of childIds) {
    const child = collectionsById.get(childId);
    if (!child) continue;

    const d2dNo = String(child.collectionNo || childId).trim();
    const boxes = Array.isArray(child.boxes) ? child.boxes : [];
    const childTotals = sumBoxTotals(boxes);
    grandPcs += childTotals.totalPcs;
    grandKg += childTotals.totalKg;
    grandCbm += childTotals.totalCbm;

    if (boxes.length === 0) {
      globalSubNo += 1;
      blockRows.push({
        key: `${parent._id || masterNo}-${childId}-empty`,
        d2dNo,
        subNo: String(globalSubNo),
        length: '',
        width: '',
        height: '',
        kg: '',
        cbm: '',
        cargoType: '',
        description: 'No boxes in collection',
        d2dGroupSize: 1,
        isFirstInD2dGroup: true,
      });
      continue;
    }

    const expanded = [];
    boxes.forEach((b, boxIdx) => {
      const qty = boxQty(b);
      for (let u = 0; u < qty; u += 1) {
        expanded.push({ b, boxIdx, u });
      }
    });

    const d2dGroupSize = expanded.length;
    for (let idx = 0; idx < expanded.length; idx += 1) {
      globalSubNo += 1;
      const item = expanded[idx];
      const { b } = item;
      const w = parseFloat(String(b.weight || '').replace(/,/g, '')) || 0;
      const cbm = parseFloat(String(b.cbm || '').replace(/,/g, '')) || 0;
      blockRows.push({
        key: `${parent._id || masterNo}-${childId}-${item.boxIdx}-${item.u}`,
        d2dNo,
        subNo: String(globalSubNo),
        length: String(b.length ?? '').trim(),
        width: String(b.width ?? '').trim(),
        height: String(b.height ?? '').trim(),
        kg: w.toFixed(2),
        cbm: cbm.toFixed(2),
        cargoType: String(b.boxType || b.officialBoxName || '').trim(),
        description: String(b.description || '').trim(),
        d2dGroupSize,
        isFirstInD2dGroup: idx === 0,
      });
    }
  }

  const masterGroupSize = blockRows.length || 1;

  return blockRows.map((row, idx) => ({
    key: row.key,
    collectionNo: masterNo,
    masterCollectionNo: masterNo,
    masterGroupSize,
    isFirstInMaster: idx === 0,
    d2dCollectionNo: row.d2dNo,
    d2d: row.d2dNo,
    subNo: row.subNo,
    length: row.length,
    width: row.width,
    height: row.height,
    kg: row.kg,
    cbm: row.cbm,
    cargoType: row.cargoType,
    description: row.description,
    clearingType: 'DOOR TO DOOR',
    d2dGroupSize: row.d2dGroupSize,
    isFirstInD2dGroup: row.isFirstInD2dGroup,
    groupSize: row.d2dGroupSize,
    isFirstInGroup: row.isFirstInD2dGroup,
    pcs: idx === 0 ? String(grandPcs) : '',
    fWeight: idx === 0 ? grandKg.toFixed(2) : '',
    fCbm: idx === 0 ? grandCbm.toFixed(2) : '',
    isConsolidatedLayout: true,
  }));
}

/**
 * @param {string[]} linkedCollectionIds
 * @param {Map<string, object>} collectionsById
 * @returns {Array<object>}
 */
export function buildPackingRowsFromLinkedCollections(linkedCollectionIds, collectionsById) {
  const rows = [];
  const idOrder = (linkedCollectionIds || []).map(String);

  for (const cid of idOrder) {
    const c = collectionsById.get(cid);
    if (!c) continue;
    if (c.consolidatedInto) continue;

    if (isConsolidatedParent(c)) {
      rows.push(...buildConsolidatedPackingRows(c, collectionsById));
    } else {
      rows.push(...buildRegularPackingRows(c, cid));
    }
  }

  return rows;
}

export function safeFileStem(shipmentCode, trackingFallback) {
  const raw = String(shipmentCode || trackingFallback || 'packing-list').replace(/[^\w-]+/g, '_');
  return raw.slice(0, 80) || 'packing-list';
}
